import { createContext, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Bell, ShieldCheck } from "lucide-react";
import { useGenesisCountdown, pad2 } from "@/lib/genesis";
import { XMark } from "./Logo";

const Ctx = createContext({ open: () => {} });
export const useLaunchModal = () => useContext(Ctx);

/* Pre-launch interaction system: trading / signup / login / notify views.
   NOTE (backend hook): the notify flow is UI-only — wire the submit handler
   in `NotifyView` to an email/notification service when one is connected. */
export default function LaunchModalProvider({ children }) {
    const [view, setView] = useState(null);
    const open = (v) => setView(v);
    const close = () => setView(null);

    useEffect(() => {
        document.body.style.overflow = view ? "hidden" : "";
        if (window.__lenis) view ? window.__lenis.stop() : window.__lenis.start();
        const onKey = (e) => e.key === "Escape" && setView(null);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [view]);

    return (
        <Ctx.Provider value={{ open, close }}>
            {children}
            <AnimatePresence>
                {view && <Modal key={view} view={view} setView={setView} close={close} />}
            </AnimatePresence>
        </Ctx.Provider>
    );
}

const MiniCountdown = () => {
    const { days, hours, minutes, seconds } = useGenesisCountdown();
    const cells = [
        [days, "Days"],
        [hours, "Hours"],
        [minutes, "Min"],
        [seconds, "Sec"],
    ];
    return (
        <div data-testid="modal-countdown" className="mt-7 grid grid-cols-4 gap-2">
            {cells.map(([v, l]) => (
                <div key={l} className="rounded-lg border border-white/10 bg-white/[0.03] py-2.5 text-center">
                    <div className="font-mono text-lg font-semibold tabular-nums text-white">{pad2(v)}</div>
                    <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-slate-500">{l}</div>
                </div>
            ))}
        </div>
    );
};

const DateLine = () => (
    <div className="mt-6 font-grotesk text-2xl font-bold uppercase tracking-[0.08em] text-grad-blue">
        28 October 2026
    </div>
);

function NotifyView({ back, backLabel }) {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [invalid, setInvalid] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
        setInvalid(!ok);
        if (!ok) return;
        setDone(true);
        // Backend hook: POST email to a notification service here when connected.
    };

    if (done) {
        return (
            <div className="text-center" data-testid="notify-success">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-cyber/40 bg-cyber/10">
                    <ShieldCheck className="h-5 w-5 text-cyber" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-grotesk text-xl font-bold uppercase tracking-[0.1em] text-white">
                    Thank you.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">
                    Registration and trading access open on 28 October 2026 — launch
                    updates will be announced here first.
                </p>
                <button
                    onClick={back}
                    data-testid="notify-done-btn"
                    className="mt-8 w-full rounded border border-white/15 py-3 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:border-cyber/50 hover:text-cyber"
                >
                    {backLabel}
                </button>
            </div>
        );
    }

    return (
        <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-cyber/40 bg-cyber/10">
                <Bell className="h-5 w-5 text-cyber" strokeWidth={1.5} />
            </div>
            <h3 id="launch-modal-title" data-testid="modal-title" className="mt-6 font-grotesk text-xl font-bold uppercase tracking-[0.1em] text-white">
                Get launch notification
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
                Leave your email and be first in line when the TAVONX ecosystem opens.
            </p>
            <form onSubmit={submit} className="mt-7 text-left">
                <label
                    htmlFor="launch-email"
                    className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500"
                >
                    Email address
                </label>
                <input
                    type="email"
                    id="launch-email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="modal-email-input"
                    className={`mt-2 w-full rounded border bg-white/[0.03] px-3.5 py-3 font-mono text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-200 ${
                        invalid ? "border-bear/60" : "border-white/12 focus:border-cyber/60"
                    }`}
                />
                {invalid && (
                    <p className="mt-1.5 font-mono text-[10px] text-bear">Please enter a valid email address.</p>
                )}
                <button
                    type="submit"
                    data-testid="modal-notify-submit"
                    className="group mt-4 flex w-full items-center justify-center gap-2 rounded bg-gradient-to-r from-electric to-cyber py-3.5 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-void transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,209,255,0.4)]"
                >
                    Notify Me
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
            </form>
            <button
                onClick={back}
                className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-slate-300"
            >
                ← {backLabel}
            </button>
        </div>
    );
}

function Modal({ view, setView, close }) {
    const openNotify = () => setView("notify");

    const titles = {
        trading: "TAVONX IS ALMOST HERE.",
        signup: "REGISTRATION OPENS SOON",
        login: "LOGIN WILL BE AVAILABLE AT LAUNCH",
        notify: "GET LAUNCH NOTIFICATION",
        soon: "AVAILABLE AT LAUNCH",
    };

    const backLabel = view === "login" ? "Back to Tavonx" : "Back";

    return (
        <motion.div
            data-testid="launch-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
        >
            <div className="absolute inset-0 bg-void/85 backdrop-blur-md" />

            <motion.div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="launch-modal-title"
                initial={{ opacity: 0, scale: 0.93, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md"
            >
                {/* animated glow behind the modal */}
                <div className="pointer-events-none absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.2),rgba(139,92,246,0.1)_55%,transparent_75%)] blur-2xl" />

                <div className="relative overflow-hidden rounded-xl border border-cyber/25 bg-[linear-gradient(180deg,#0D1424_0%,#0A0F1D_100%)] p-7 text-center shadow-[0_40px_120px_rgba(0,0,0,0.8)] sm:p-9">
                    <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyber/80 to-transparent" />

                    <button
                        onClick={close}
                        data-testid="modal-close"
                        autoFocus
                        aria-label="Close"
                        className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded border border-white/10 text-slate-400 transition-all duration-200 hover:border-cyber/50 hover:text-cyber"
                    >
                        <X className="h-4 w-4" />
                    </button>

                    {view === "notify" ? (
                        <NotifyView back={() => setView("trading")} backLabel="Back" />
                    ) : (
                        <div>
                            <div className="mx-auto w-fit">
                                <XMark className="h-10 w-auto" />
                            </div>

                            <h3 id="launch-modal-title" data-testid="modal-title" className="mt-6 font-grotesk text-xl font-bold uppercase leading-snug tracking-[0.08em] text-white">
                                {titles[view]}
                            </h3>

                            {view === "trading" && (
                                <div data-testid="modal-body-trading">
                                    <p className="mt-5 text-sm leading-relaxed text-slate-400">
                                        Trading goes live on
                                    </p>
                                    <DateLine />
                                    <p className="mt-4 text-sm leading-relaxed text-slate-400">
                                        Be ready for the next generation of digital markets.
                                    </p>
                                    <MiniCountdown />
                                    <button
                                        onClick={openNotify}
                                        data-testid="modal-cta-notify"
                                        className="group mt-7 flex w-full items-center justify-center gap-2 rounded bg-gradient-to-r from-electric to-cyber py-3.5 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-void transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,209,255,0.4)]"
                                    >
                                        Get Launch Notification
                                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                    </button>
                                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">
                                        Trading access will be available at launch.
                                    </p>
                                </div>
                            )}

                            {view === "signup" && (
                                <div data-testid="modal-body-signup">
                                    <p className="mt-5 text-sm leading-relaxed text-slate-400">
                                        We're preparing the TAVONX platform for launch.
                                    </p>
                                    <p className="mt-4 text-sm text-slate-400">Registration will open on</p>
                                    <DateLine />
                                    <button
                                        onClick={openNotify}
                                        data-testid="modal-cta-notify"
                                        className="group mt-8 flex w-full items-center justify-center gap-2 rounded bg-gradient-to-r from-electric to-cyber py-3.5 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-void transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,209,255,0.4)]"
                                    >
                                        Get Launch Notification
                                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                    </button>
                                </div>
                            )}

                            {view === "login" && (
                                <div data-testid="modal-body-login">
                                    <p className="mt-5 text-sm leading-relaxed text-slate-400">
                                        TAVONX account access will become available when the
                                        platform goes live.
                                    </p>
                                    <DateLine />
                                    <button
                                        onClick={close}
                                        data-testid="modal-cta-back"
                                        className="mt-8 w-full rounded border border-white/15 py-3.5 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:border-cyber/50 hover:text-cyber"
                                    >
                                        Back to Tavonx
                                    </button>
                                </div>
                            )}

                            {view === "soon" && (
                                <div data-testid="modal-body-soon">
                                    <p className="mt-5 text-sm leading-relaxed text-slate-400">
                                        This part of the TAVONX experience goes live with the
                                        platform.
                                    </p>
                                    <DateLine />
                                    <button
                                        onClick={openNotify}
                                        data-testid="modal-cta-notify"
                                        className="group mt-8 flex w-full items-center justify-center gap-2 rounded bg-gradient-to-r from-electric to-cyber py-3.5 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-void transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,209,255,0.4)]"
                                    >
                                        Get Launch Notification
                                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}
